import api from './api';

/*
| Support content and the AI assistant.
|
| The content used to be a JavaScript array in components/support/supportContent.js, and a
| near-identical array lived in the marketing site. Two copies of the same answers meant
| every edit had to be made twice, in two repositories — so it was made once, and the two
| pages drifted. The answers now live in the API (resources/ai_support) and every surface
| reads them from here, which is also what the assistant answers from. One edit, one truth.
|
| API paths use underscores per the URL naming rule.
*/

const URL = '/ai_support';

/*
| Is the assistant switched on?
|
| Memoised for the page load. The header launcher has to know before it can decide whether
| to render a button, and it mounts on every route — without this it would ask on every
| navigation for an answer that cannot change within a session.
|
| A failure resolves to false rather than rejecting: the launcher simply does not appear,
| which is the correct outcome for "we could not confirm the assistant is available".
*/
let capabilityPromise = null;

class SupportService {
    /** Categories, topics and quick fixes for the in-app audience. */
    getContent() {
        return api.get(`${URL}/topics`);
    }

    /**
     * Whether the assistant is available, asked once per page load.
     *
     * @returns {Promise<boolean>}
     */
    isAvailable() {
        if (!capabilityPromise) {
            capabilityPromise = this.getContent()
                .then((response) => !!response?.data?.data?.extra?.ai_enabled)
                .catch(() => false);
        }

        return capabilityPromise;
    }

    /**
     * Ask the assistant a question.
     *
     * Pass `conversationPublicId` from the previous answer to keep one thread — the API
     * checks the conversation belongs to the caller before continuing it, so a stale id
     * silently starts a new one rather than failing.
     */
    ask(question, conversationPublicId = null) {
        return api.post(`${URL}/messages`, {
            question,
            conversation_public_id: conversationPublicId,
            source_url: window.location.pathname,
        });
    }

    /**
     * Hand the conversation to a person.
     *
     * Name and email are not sent: the caller is signed in, so the API takes them from the
     * account. That is both less to type and better data — the address on the account is
     * the one that actually reaches them.
     */
    escalate(conversationPublicId, note = null) {
        return api.post(`${URL}/escalate`, {
            conversation_public_id: conversationPublicId,
            note,
        });
    }
}

export default new SupportService();
